import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Calendar, ClockCountdown, Fire } from "@phosphor-icons/react";

const STAGES = [
  { value: "cold", label: "Cold", color: "bg-slate-500" },
  { value: "warm", label: "Warm", color: "bg-amber-500" },
  { value: "hot", label: "Hot", color: "bg-orange-500" },
  { value: "follow_up", label: "Follow-up", color: "bg-blue-500" },
  { value: "lost", label: "Lost", color: "bg-rose-500" },
];

/**
 * Renders in the Inbox right-hand sidebar under the Lead card.
 * Lets the agent schedule/reschedule the next contact date + note + nurturing stage.
 */
export default function NextContactPanel({ leadId, onUpdated }) {
  const [lead, setLead] = useState(null);
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const [stage, setStage] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!leadId) return;
    try {
      const { data } = await api.get(`/leads/${leadId}`);
      setLead(data);
      setDate(data.next_contact_date || "");
      setNote(data.next_contact_note || "");
      setStage(data.nurturing_stage || "");
    } catch {
      setLead(null);
    }
  };

  useEffect(() => { load(); }, [leadId]);

  if (!leadId || !lead) return null;

  const save = async () => {
    if (!date) return toast.error("Elige una fecha");
    setSaving(true);
    try {
      await api.post(`/leads/${leadId}/schedule-contact`, {
        next_contact_date: date,
        note,
        nurturing_stage: stage || undefined,
      });
      toast.success("Próximo contacto programado");
      load();
      onUpdated?.();
    } catch {
      toast.error("Error programando");
    } finally { setSaving(false); }
  };

  const todayISO = new Date().toISOString().slice(0, 10);
  const isDue = lead.next_contact_date && lead.next_contact_date <= todayISO;

  const stageMeta = STAGES.find((s) => s.value === lead.nurturing_stage);

  return (
    <div className="border border-border rounded-md p-3 space-y-2 mt-4" data-testid="next-contact-panel">
      <div className="flex items-center justify-between">
        <div className="overline flex items-center gap-1.5">
          <Calendar size={11} weight="fill" className="text-primary" /> Próximo contacto
        </div>
        {isDue && <Badge className="bg-amber-500 text-white text-[10px] gap-1"><ClockCountdown size={11} /> hoy</Badge>}
      </div>

      {stageMeta && (
        <div className="flex items-center gap-1.5">
          <Badge className={`${stageMeta.color} text-white text-[10px]`}>
            <Fire size={10} className="mr-0.5" weight="fill" /> {stageMeta.label}
          </Badge>
        </div>
      )}

      <div>
        <label className="text-[10px] uppercase text-muted-foreground">Fecha</label>
        <Input
          type="date"
          value={date}
          min={todayISO}
          onChange={(e) => setDate(e.target.value)}
          className="text-xs h-9"
          data-testid="next-contact-date"
        />
      </div>

      <div>
        <label className="text-[10px] uppercase text-muted-foreground">Nota</label>
        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          placeholder="Llamar para confirmar test-drive..."
          className="text-xs"
          data-testid="next-contact-note"
        />
      </div>

      <div>
        <label className="text-[10px] uppercase text-muted-foreground">Etapa (opc.)</label>
        <Select value={stage} onValueChange={setStage}>
          <SelectTrigger className="h-8 text-xs" data-testid="next-contact-stage"><SelectValue placeholder="Sin cambio" /></SelectTrigger>
          <SelectContent>
            {STAGES.map((s) => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      <Button onClick={save} disabled={saving || !date} className="w-full h-8 text-xs" data-testid="next-contact-save">
        {saving ? "Guardando…" : "Programar"}
      </Button>

      {lead.follow_up_history?.length > 0 && (
        <div className="pt-2 border-t border-border mt-2">
          <div className="overline mb-1">Historial ({lead.follow_up_history.length})</div>
          <div className="space-y-1 max-h-24 overflow-auto">
            {lead.follow_up_history.slice(-3).reverse().map((h, i) => (
              <div key={i} className="text-[10px] text-muted-foreground">
                <span className="font-mono">{h.date}</span> · {h.note || "sin nota"} · {h.by}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
